import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { startGoogleLogin } from "@/lib/googleAuth";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "",
    twenty_min_power: "", monthly_hours: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({
        ...form,
        twenty_min_power: form.twenty_min_power ? Number(form.twenty_min_power) : 0,
        monthly_hours: form.monthly_hours ? Number(form.monthly_hours) : 0,
      });
      toast.success("Account creato. Bentornato in sella!");
      nav("/feed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => startGoogleLogin();

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 md:p-12 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-lg space-y-5">
          <div>
            <div className="label-tag mb-2">Crea account</div>
            <h2 className="font-display font-black text-4xl uppercase tracking-tighter text-gray-900">Iscriviti</h2>
            <p className="text-gray-500 mt-2 text-sm">Pochi dati, e sei in sella.</p>
          </div>

          <button
            type="button"
            onClick={googleLogin}
            data-testid="register-google-btn"
            className="w-full bg-white text-black font-bold uppercase tracking-wider text-sm py-3 rounded-sm hover:bg-gray-200"
          >
            Iscriviti con Google
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/15" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-500">oppure</span>
            <div className="flex-1 h-px bg-white/15" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-tag block mb-2">Nome</label>
              <input data-testid="register-first-name" required value={form.first_name} onChange={update("first_name")} className="w-full bg-white border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
            </div>
            <div>
              <label className="label-tag block mb-2">Cognome</label>
              <input data-testid="register-last-name" required value={form.last_name} onChange={update("last_name")} className="w-full bg-white border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
            </div>
          </div>

          <div>
            <label className="label-tag block mb-2">Email</label>
            <input data-testid="register-email" type="email" required value={form.email} onChange={update("email")} className="w-full bg-white border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
          </div>

          <div>
            <label className="label-tag block mb-2">Password (min 6)</label>
            <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={update("password")} className="w-full bg-white border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-tag block mb-2">20 min Power (W)</label>
              <input data-testid="register-power" type="number" min="0" max="600" value={form.twenty_min_power} onChange={update("twenty_min_power")} placeholder="es. 250" className="w-full bg-white border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
            </div>
            <div>
              <label className="label-tag block mb-2">Ore in sella / mese</label>
              <input data-testid="register-hours" type="number" min="0" max="200" step="0.5" value={form.monthly_hours} onChange={update("monthly_hours")} placeholder="es. 12" className="w-full bg-white border border-gray-200 focus:border-[#E11D48] focus:outline-none rounded-sm px-4 py-3 text-gray-900" />
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Suggerimento: collega Garmin dal profilo per aggiornare automaticamente queste statistiche.
          </p>

          <button data-testid="register-submit-btn" type="submit" disabled={loading} className="w-full btn-primary py-3 rounded-sm disabled:opacity-50">
            {loading ? "Creazione..." : "Crea account"}
          </button>

          <p className="text-center text-sm text-gray-500">
            Hai già un account? <Link to="/login" className="text-[#E11D48] font-semibold hover:underline" data-testid="register-go-login">Accedi</Link>
          </p>
        </form>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1673890704132-9573476ac27c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)" }} />
        <div className="absolute inset-0 bg-gradient-to-bl from-black via-black/70 to-black/30" />
        <div className="relative h-full flex items-end p-12">
          <div>
            <h1 className="font-display font-black text-5xl uppercase tracking-tighter text-white leading-[0.9]">
              Unisciti al<br/><span className="text-[#E11D48]">gruppo</span>.
            </h1>
            <p className="text-gray-700 mt-4 max-w-md">Migliaia di km condivisi. Trova chi pedala come te.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
